import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { useLocale } from "../i18n/useLocale";
import { Modal } from "../ui/Modal";
import { buildWorkshopHandoff } from "./workshopHandoff";

export function WorkshopHandoffPanel({
  open,
  onClose,
  project,
}) {
  const { display } = useLocale();
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [copyState, setCopyState] = useState("idle");

  const handoff = useMemo(
    () => (open && project ? buildWorkshopHandoff(project) : null),
    [open, project],
  );

  useEffect(() => {
    if (!open) {
      setQrDataUrl("");
      setCopyState("idle");
    }
  }, [open]);

  useEffect(() => {
    let cancelled = false;

    async function renderQr() {
      if (!handoff) {
        setQrDataUrl("");
        return;
      }

      try {
        const dataUrl = await QRCode.toDataURL(handoff.deepLink, {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 280,
        });

        if (!cancelled) {
          setQrDataUrl(dataUrl);
        }
      } catch {
        if (!cancelled) {
          setQrDataUrl("");
        }
      }
    }

    renderQr();
    return () => {
      cancelled = true;
    };
  }, [handoff]);

  if (!open || !handoff) {
    return null;
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(handoff.deepLink);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 2000);
    } catch {
      setCopyState("failed");
    }
  };

  return (
    <Modal
      backdropClassName="handoff-overlay"
      className="handoff-panel"
      header={(
        <div className="handoff-header">
          <div>
            <h2 id="handoff-title">{display.t("web.workshopHandoff.title")}</h2>
            <p className="handoff-lead">
              {display.t("web.workshopHandoff.lead")}
            </p>
          </div>
          <button type="button" className="secondary-button" onClick={onClose}>
            {display.t("web.workshopHandoff.close")}
          </button>
        </div>
      )}
      onClose={onClose}
      titleId="handoff-title"
    >
      <div className="handoff-body">
        <div className="handoff-qr-wrap">
          {qrDataUrl ? (
            <img alt={display.t("web.workshopHandoff.qrAlt")} src={qrDataUrl} />
          ) : (
            <div className="handoff-qr-placeholder">
              {display.t("web.workshopHandoff.qrLoading")}
            </div>
          )}
        </div>

        <div className="handoff-actions">
          <a className="primary-button handoff-open-button" href={handoff.deepLink}>
            {display.t("web.workshopHandoff.openInApp")}
          </a>
          <button type="button" className="secondary-button" onClick={copyLink}>
            {copyState === "copied"
              ? display.t("web.workshopHandoff.linkCopied")
              : display.t("web.workshopHandoff.copyLink")}
          </button>
          {copyState === "failed" ? (
            <p className="handoff-note handoff-note--error" role="alert">
              {display.t("web.workshopHandoff.copyFailed")}
            </p>
          ) : null}
          {copyState === "copied" ? (
            <p className="handoff-note" role="status">
              {display.t("web.workshopHandoff.linkCopied")}
            </p>
          ) : null}
          <p className="handoff-note">
            {display.t("web.workshopHandoff.note")}
          </p>
        </div>
      </div>
    </Modal>
  );
}
